(() => {
  const allowedHosts = [
    'sembonia.com',
    'www.sembonia.com',
    'int.sembonia.com'
  ];

  if (!allowedHosts.includes(location.hostname)) return;
  if (window.semboniaCurrencyLoaded) return;

  const config = window.semboniaCurrencyConfig;

  if (!config || config.designMode) return;

  window.semboniaCurrencyLoaded = true;

  const countryCurrencies = {
    MY: 'MYR',
    SG: 'SGD',
    AU: 'AUD',
    HK: 'HKD',
    US: 'USD',
    TH: 'THB',
    ID: 'IDR',
    PH: 'PHP',
    GB: 'GBP',
    CA: 'CAD',
    NZ: 'NZD',
    CN: 'CNY',
    TW: 'TWD',
    JP: 'JPY',
    KR: 'KRW',
    BN: 'BND',
    VN: 'VND',
    IN: 'INR',
    CH: 'CHF',
    AE: 'AED',
    AT: 'EUR',
    BE: 'EUR',
    HR: 'EUR',
    CY: 'EUR',
    EE: 'EUR',
    FI: 'EUR',
    FR: 'EUR',
    DE: 'EUR',
    GR: 'EUR',
    IE: 'EUR',
    IT: 'EUR',
    LV: 'EUR',
    LT: 'EUR',
    LU: 'EUR',
    MT: 'EUR',
    NL: 'EUR',
    PT: 'EUR',
    SK: 'EUR',
    SI: 'EUR',
    ES: 'EUR',
    BG: 'EUR'
  };

  async function fetchJSON(url, timeoutMs, noCache = false) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'omit',
        cache: noCache ? 'no-store' : 'default',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Request failed: HTTP ${response.status}`);
      }

      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  function redirectToHost(targetHost) {
    if (location.hostname === targetHost) return false;

    const destination = new URL(location.href);
    destination.protocol = 'https:';
    destination.hostname = targetHost;
    destination.port = '';
    destination.searchParams.delete('country');

    // 在 URL 中带一次跳转标记，避免两个域名互相跳转。
    const guardParameter = '_sembonia_geo';
    const previous = Number(
      destination.searchParams.get(guardParameter)
    );
    const now = Date.now();

    if (
      previous > 0 &&
      previous <= now &&
      now - previous < 60000
    ) {
      console.warn('Repeated country redirect stopped.');
      return true;
    }

    destination.searchParams.set(guardParameter, String(now));

    location.replace(destination.href);
    return true;
  }

  function removeRedirectMarker() {
    const current = new URL(location.href);

    if (!current.searchParams.has('_sembonia_geo')) return;

    current.searchParams.delete('_sembonia_geo');

    try {
      history.replaceState(history.state, '', current.href);
    } catch {
      // 无法修改 URL 时不影响价格显示。
    }
  }

  async function start() {
    try {
      /*
       * 1. 读取真实 IP 国家。
       * 独立请求，不与汇率一起缓存。
       */
      const locationURL = new URL(
        '/location',
        config.endpoint
      ).href;

      const geo = await fetchJSON(locationURL, 8000, true);

      const country = String(
        geo.country || ''
      ).toUpperCase();

      if (
        geo.status !== 'Success' ||
        !/^[A-Z]{2}$/.test(country) ||
        ['XX', 'T1'].includes(country)
      ) {
        throw new Error('Unable to identify visitor country');
      }

      /*
       * 2. Malaysia → 主站。
       * 其他国家 → int。
       * 保留原本页面路径及其他查询参数。
       */
      const targetHost = country === 'MY'
        ? 'sembonia.com'
        : 'int.sembonia.com';

      if (redirectToHost(targetHost)) return;

      removeRedirectMarker();

      // Malaysia 保持原本 MYR 显示。
      if (country === 'MY') return;

      // 只允许从 MYR 原始金额进行换算。
      if (config.baseCurrency !== 'MYR') {
        console.warn(
          'Currency conversion skipped: base currency is not MYR.'
        );
        return;
      }

      /*
       * 3. 获取汇率。
       */
      const data = await fetchJSON(
        config.endpoint,
        12000
      );

      if (
        data.status !== 'Success' ||
        !data.currencyList
      ) {
        throw new Error('Invalid currency response');
      }

      const rates = data.currencyList;

      function getRate(code) {
        const item = rates[code];
        const rate = Number(item?.rate);

        if (
          item?.from === 'MYR' &&
          item?.to === code &&
          Number.isFinite(rate) &&
          rate > 0
        ) {
          return rate;
        }

        return null;
      }

      /*
       * 4. 有对应汇率 → 当地货币。
       * 没有对应汇率 → USD。
       */
      const mappedCurrency = countryCurrencies[country] || 'USD';
      const preferred = mappedCurrency === 'EUR' ? 'USD' : mappedCurrency;

      const currency = getRate(preferred) !== null
        ? preferred
        : 'USD';

      const rate = getRate(currency);

      if (rate === null) {
        throw new Error('No valid USD fallback rate');
      }

      const formatter = new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        {
          style: 'currency',
          currency,
          currencyDisplay: 'code'
        }
      );

      /*
       * 5. 始终使用 data-amount 原价计算。
       * 不从已经转换过的文字再次换算。
       */
      function updatePrices() {
        document
          .querySelectorAll('[data-sembonia-money]')
          .forEach(element => {
            if (element.dataset.baseCurrency !== 'MYR') return;

            const raw = element.getAttribute('data-amount');

            if (raw === null || raw.trim() === '') return;

            const amount = Number(raw);

            if (
              !Number.isFinite(amount) ||
              amount < 0
            ) {
              return;
            }

            const output = formatter.format(
              (amount / 100) * rate
            );

            if (element.textContent !== output) {
              element.textContent = output;
            }
          });
      }

      /*
       * 6. 监听动态插入或更新的价格标记。
       */
      let scheduled = false;

      const observer = new MutationObserver(() => {
        if (scheduled) return;

        scheduled = true;

        requestAnimationFrame(() => {
          scheduled = false;
          observer.disconnect();

          try {
            updatePrices();
          } finally {
            observe();
          }
        });
      });

      function observe() {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'data-amount',
            'data-base-currency'
          ]
        });
      }

      updatePrices();
      observe();

      window.semboniaDisplayCurrency = currency;
      window.semboniaDetectedCountry = country;

    } catch (error) {
      // 接口失败时保留页面原本价格，不显示错误换算金额。
      console.warn(
        'Country/currency service unavailable; original prices retained.',
        error?.message || String(error)
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();